export const UNFILED = "unfiled";

// The shortest Stale Assets filter, so the card counts every stale document.
export const STALE_AFTER_MONTHS = 6;

// Fixed display order for the system folders, plus what each one is meant to hold.
export const SYSTEM_FOLDERS = [
  { name: "Team Structure", accepts: "Org charts, CVs, key-personnel bios" },
  { name: "Financial", accepts: "Audited financials, balance sheets, P&L, tax filings" },
  { name: "Project Delivery Methodology", accepts: "Delivery playbooks, PM frameworks, QA plans" },
  { name: "Compliance", accepts: "Certificates, registrations, licences, policies" },
  { name: "Previous Proposals", accepts: "Submitted proposals and bid responses" },
  { name: "Technical Capabilities", accepts: "Architecture docs, product and platform specs" },
  { name: "Case Studies", accepts: "Client case studies, project references" },
];

export const systemFolderAccepts = (name) =>
  SYSTEM_FOLDERS.find((f) => f.name === name)?.accepts || "";

const systemRank = (name) => {
  const i = SYSTEM_FOLDERS.findIndex((f) => f.name === name);
  return i === -1 ? SYSTEM_FOLDERS.length : i;
};

// A saved position (drag to reorder) wins; folders never reordered keep the default order after them.
const byPosition = (fallback) => (a, b) => {
  const pa = Number.isInteger(a.position) ? a.position : Infinity;
  const pb = Number.isInteger(b.position) ? b.position : Infinity;
  return pa !== pb ? pa - pb : fallback(a, b);
};

export const orderFolders = (folders) => {
  const system = folders
    .filter((f) => f.is_default)
    .sort(byPosition((a, b) => systemRank(a.name) - systemRank(b.name)));
  const own = folders
    .filter((f) => !f.is_default)
    .sort(byPosition((a, b) => new Date(a.created_at) - new Date(b.created_at) || a.id - b.id));
  return [...system, ...own];
};

export const applyDocumentOrder = (docs, order) => {
  if (!order || !order.length) return docs;
  const rank = new Map(order.map((id, i) => [id, i]));
  const last = order.length;
  return [...docs].sort(
    (a, b) => (rank.has(a.id) ? rank.get(a.id) : last) - (rank.has(b.id) ? rank.get(b.id) : last)
  );
};

export const sha256Hex = async (file) => {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

export const statusKey = (raw) => {
  const s = (raw || "").toLowerCase();
  if (s === "ready_for_review" || s === "ready for review") return "review";
  if (s === "ready" || s === "ingested" || s === "completed") return "ingested";
  if (s === "error" || s === "failed") return "error";
  return "processing";
};

const STATUS_RANK = { review: 0, error: 1, processing: 2, ingested: 3 };

const addedTime = (d) => new Date(d.uploaded_at || d.created_at || 0).getTime();

export const sortDocuments = (list, key, dir) => {
  const m = dir === "asc" ? 1 : -1;
  return [...list].sort((a, b) => {
    let r = 0;
    if (key === "name") {
      r = (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base", numeric: true });
    } else if (key === "status") {
      r = STATUS_RANK[statusKey(a.status)] - STATUS_RANK[statusKey(b.status)];
    } else {
      r = addedTime(a) - addedTime(b);
    }
    return r * m || addedTime(b) - addedTime(a) || (b.id || 0) - (a.id || 0);
  });
};

export const monthsSince = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 0;
  const now = new Date();
  return (now.getFullYear() - d.getFullYear()) * 12 + now.getMonth() - d.getMonth();
};
